import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { ApiHandlerService } from '@core/handlers/api-handler.service';
import { PlanUsage } from '@core/models/plan-usage.model';

@Injectable({
  providedIn: 'root'
})
export class PlanUsageService {
  private planUsage$ = new BehaviorSubject<PlanUsage | null>(null);
  private loadedCompanyId: string | null = null;

  constructor(private apiService: ApiHandlerService) {}

  /**
   * Get plan usage for the current company.
   * Caches the result and reloads only when companyId changes.
   */
  getPlanUsage(companyId: string): Observable<PlanUsage> {
    if (this.loadedCompanyId === companyId && this.planUsage$.value) {
      return of(this.planUsage$.value);
    }

    return this.apiService
      .get<PlanUsage>(`${this.apiService.apiCompanyUrl()}/${companyId}/plan-usage`)
      .pipe(
        tap(usage => {
          this.loadedCompanyId = companyId;
          this.planUsage$.value !== usage && this.planUsage$.next(usage);
        })
      );
  }

  /**
   * Force reload plan usage (e.g. after plan change)
   */
  invalidate(): void {
    this.loadedCompanyId = null;
    this.planUsage$.next(null);
  }

  /** Shortcut checks */
  get cached(): PlanUsage | null {
    return this.planUsage$.value;
  }

  canBatchImport(): boolean {
    return this.planUsage$.value?.batchImport ?? true;
  }

  canInitialLoad(): boolean {
    return this.planUsage$.value?.initialLoad ?? true;
  }

  canReportsAllTypes(): boolean {
    return this.planUsage$.value?.reportsAllTypes ?? false;
  }

  canReportsPdf(): boolean {
    return this.planUsage$.value?.reportsPdf ?? false;
  }

  canIfrs16(): boolean {
    return this.planUsage$.value?.ifrs16 ?? false;
  }

  getAuditRetentionDays(): number {
    return this.planUsage$.value?.auditRetentionDays ?? 0;
  }

  canRevaluation(): boolean {
    return this.planUsage$.value?.revaluation ?? false;
  }

  canImpairment(): boolean {
    return this.planUsage$.value?.impairment ?? false;
  }

  canThirdPartyAssets(): boolean {
    return this.planUsage$.value?.thirdPartyAssets ?? true;
  }

  canIntangibles(): boolean {
    return this.planUsage$.value?.intangibles ?? true;
  }
}
